<template>
  <div class="sheet-overlay" @click.self="$emit('close')">
    <div class="sheet-container">
      <!-- 핸들바 -->
      <div class="sheet-handle">
        <div class="handle-bar"></div>
      </div>

      <!-- 헤더 -->
      <div class="sheet-header">
        <button class="back-btn" @click="$emit('close')">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
        <h2 class="sheet-title">상담 신청</h2>
        <div class="w-10"></div>
      </div>

      <!-- 컨텐츠 -->
      <div class="sheet-content">
        <!-- 입력 폼 -->
        <form class="request-form" @submit.prevent="handleSubmit">
          <!-- 이름 -->
          <div class="form-group">
            <label class="form-label">이름 <span class="required">*</span></label>
            <input
              v-model="form.name"
              type="text"
              class="form-input"
              placeholder="실명을 입력해주세요"
              required
            />
          </div>

          <!-- 전화번호 -->
          <div class="form-group">
            <label class="form-label">연락처 <span class="required">*</span></label>
            <input
              v-model="form.phone"
              type="tel"
              class="form-input"
              placeholder="010-0000-0000"
              required
              @input="formatPhone"
            />
          </div>

          <!-- 동의 체크박스 -->
          <div class="consent-section">
            <h4 class="consent-title">약관 동의</h4>

            <!-- 개인정보 수집/이용 동의 -->
            <label class="consent-item">
              <input
                v-model="form.privacyAgreed"
                type="checkbox"
                class="consent-checkbox"
                required
              />
              <div class="consent-content">
                <span class="consent-label">
                  <span class="required-badge">필수</span>
                  개인정보 수집 및 이용 동의
                </span>
                <button type="button" class="view-detail" @click="showPrivacyDetail = !showPrivacyDetail">
                  {{ showPrivacyDetail ? '닫기' : '내용보기' }}
                </button>
              </div>
            </label>
            <div v-if="showPrivacyDetail" class="consent-detail">
              <p>수집항목: 이름, 연락처</p>
              <p>수집목적: 부동산 상담 서비스 제공</p>
              <p>보유기간: 상담 완료 후 1년</p>
            </div>

            <!-- 제3자 제공 동의 (24시간 초과 시) - 캠페인 모드에서는 숨김 -->
            <template v-if="!isCampaign">
              <label class="consent-item">
                <input
                  v-model="form.transferAgreed"
                  type="checkbox"
                  class="consent-checkbox"
                  required
                />
                <div class="consent-content">
                  <span class="consent-label">
                    <span class="required-badge">필수</span>
                    24시간 초과 시 다른 상담사 연결 동의
                  </span>
                  <button type="button" class="view-detail" @click="showTransferDetail = !showTransferDetail">
                    {{ showTransferDetail ? '닫기' : '내용보기' }}
                  </button>
                </div>
              </label>
              <div v-if="showTransferDetail" class="consent-detail">
                <p>선택하신 상담사가 24시간 내 연락하지 못하는 경우,</p>
                <p>같은 현장의 다른 상담사에게 고객님의 정보가 전달됩니다.</p>
                <p>빠른 상담을 위한 조치이며, 원치 않으시면 동의를 거부하실 수 있습니다.</p>
                <p class="text-amber-600 font-medium mt-2">※ 동의 거부 시 상담 신청이 불가합니다.</p>
              </div>
            </template>
          </div>
        </form>
      </div>

      <!-- 하단 (왼쪽: 현장+상담사, 오른쪽: 버튼) -->
      <div class="sheet-footer">
        <div class="footer-info">
          <span class="footer-property">{{ property.name }}</span>
          <span class="footer-partner">{{ partner.displayName }}</span>
        </div>
        <button
          class="submit-btn"
          :disabled="!isFormValid || submitting"
          @click="handleSubmit"
        >
          <span v-if="submitting">신청 중...</span>
          <span v-else>상담 신청하기</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'ConsultationRequestSheet',
  props: {
    property: {
      type: Object,
      required: true
    },
    partner: {
      type: Object,
      required: true
    },
    isCampaign: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: String,
      default: null
    },
    campaignCode: {
      type: String,
      default: null
    },
    // 광고 유입 플래그
    adSource: {
      type: String,
      default: null,
      validator: (value) => [
        null, 'jump', 'banner', 'adbanner', 'popup', 'splash',
        'recommend', 'community', 'chatad'
      ].includes(value)
    }
  },
  emits: ['close', 'submit'],
  setup(props, { emit }) {
    const form = ref({
      name: '',
      phone: '',
      privacyAgreed: false,
      transferAgreed: false
    })

    const showPrivacyDetail = ref(false)
    const showTransferDetail = ref(false)
    const submitting = ref(false)

    const isFormValid = computed(() => {
      const baseValid =
        form.value.name.trim() !== '' &&
        form.value.phone.trim() !== '' &&
        form.value.privacyAgreed

      // 캠페인 모드에서는 transferAgreed 불필요
      if (props.isCampaign) {
        return baseValid
      }
      return baseValid && form.value.transferAgreed
    })

    const formatPhone = (e) => {
      let value = e.target.value.replace(/[^0-9]/g, '')

      if (value.length > 11) {
        value = value.slice(0, 11)
      }

      if (value.length > 7) {
        value = value.replace(/(\d{3})(\d{4})(\d{0,4})/, '$1-$2-$3')
      } else if (value.length > 3) {
        value = value.replace(/(\d{3})(\d{0,4})/, '$1-$2')
      }

      form.value.phone = value
    }

    const handleSubmit = async () => {
      if (!isFormValid.value || submitting.value) return

      submitting.value = true

      try {
        const submitData = {
          name: form.value.name.trim(),
          phone: form.value.phone.trim(),
          privacyAgreed: form.value.privacyAgreed,
          transferAgreed: props.isCampaign ? true : form.value.transferAgreed
        }

        // 캠페인 모드인 경우 캠페인 정보 추가
        if (props.isCampaign) {
          submitData.fromCampaign = true
          submitData.campaignId = props.campaignId
          submitData.campaignCode = props.campaignCode
        }

        // 광고 유입 플래그 추가
        if (props.adSource) {
          const adSourceMap = {
            'jump': 'fromJump',
            'banner': 'fromBanner',
            'adbanner': 'fromAdBanner',
            'popup': 'fromPopup',
            'splash': 'fromSplash',
            'recommend': 'fromRecommendAd',
            'community': 'fromCommunityAd',
            'chatad': 'fromChatAd'
          }
          const flagName = adSourceMap[props.adSource]
          if (flagName) {
            submitData[flagName] = true
          }
        }

        emit('submit', submitData)
      } finally {
        submitting.value = false
      }
    }

    return {
      form,
      showPrivacyDetail,
      showTransferDetail,
      submitting,
      isFormValid,
      formatPhone,
      handleSubmit
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

/* 모바일: 바텀시트 */
.sheet-overlay {
  @apply fixed inset-0 z-50 bg-black/50 flex items-end justify-center;
  animation: fadeIn 0.2s ease-out;
  padding-bottom: 64px; /* MobileTabBar 높이 */
}

.sheet-container {
  @apply w-full max-h-[85vh] bg-white rounded-t-3xl flex flex-col;
  animation: slideUp 0.3s ease-out;
}

/* 데스크탑: 중앙 모달 */
@media (min-width: 769px) {
  .sheet-overlay {
    @apply items-center;
    padding-bottom: 0;
  }

  .sheet-container {
    @apply max-w-md rounded-2xl;
    max-height: 80vh;
    animation: scaleIn 0.2s ease-out;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.sheet-handle {
  @apply flex justify-center py-3;
}

.handle-bar {
  @apply w-10 h-1 bg-slate-300 rounded-full;
}

.sheet-header {
  @apply flex items-center justify-between px-4 pb-3 border-b border-slate-100;
}

.back-btn {
  @apply p-2 text-slate-600 hover:bg-slate-100 rounded-full;
}

.sheet-title {
  @apply text-lg font-semibold text-slate-800;
}

.sheet-content {
  @apply flex-1 overflow-y-auto p-5;
}

.request-form {
  @apply space-y-5;
}

.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-slate-700;
}

.required {
  @apply text-red-500;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20;
}

.consent-section {
  @apply pt-5 border-t border-slate-100;
}

.consent-title {
  @apply text-sm font-semibold text-slate-700 mb-3;
}

.consent-item {
  @apply flex items-start gap-3 py-3 cursor-pointer;
}

.consent-checkbox {
  @apply w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-600;
}

.consent-content {
  @apply flex-1 flex items-center justify-between;
}

.consent-label {
  @apply flex items-center gap-2 text-sm text-slate-700;
}

.required-badge {
  @apply px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded;
}

.view-detail {
  @apply text-xs text-blue-600 hover:underline;
}

.consent-detail {
  @apply ml-8 mb-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 leading-relaxed space-y-1;
}

.sheet-footer {
  @apply p-3 border-t border-slate-100 flex items-center justify-between gap-3;
}

.footer-info {
  @apply flex flex-col min-w-0 flex-1;
}

.footer-property {
  @apply text-sm font-semibold text-slate-800 truncate;
}

.footer-partner {
  @apply text-xs text-slate-500 truncate;
}

.submit-btn {
  @apply px-5 py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg transition disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-blue-700 whitespace-nowrap flex-shrink-0;
}
</style>
